import React from 'react';
import {
  Action, FormHandlers, Portal, Stack, useModal,
} from '@doveit/bricks';
import { Task } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpsertTask from '../upsert-task/UpsertTask';
import { referenceTypeLabels } from '../../../labels';
import { TaskStatus, ReferenceType } from '../../../domain/types';
import ViewPropertyPreview from '../../../property/containers/view-property-preview/ViewPropertyPreview';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

interface ChildFnProps {
  upsert: VoidFunction,
  label: string,
}

export interface UpsertTaskActionProps {
  task?: Partial<Task>,
  onSuccess?: (task: Task) => void,
  children?: (props: ChildFnProps) => void,
}

const UpsertTaskAction: React.FC<UpsertTaskActionProps> = ({
  task,
  children,
  onSuccess,
}) => {
  const { user, userIsAdmin, userIsContentEditor } = useRBAC();

  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const modal = useModal();

  const isUpdate = !!task?.id;

  const canUpdate = React.useMemo(
    () => (userIsAdmin || userIsContentEditor || user.email === task?.reporter || user.email === task?.assignee) && task?.status !== TaskStatus.DONE,
    [task?.assignee, task?.reporter, task?.status, user.email, userIsAdmin, userIsContentEditor],
  );

  const operationLabel = React.useMemo(
    () => (isUpdate ? 'Modifica' : 'Aggiungi'),
    [isUpdate],
  );

  const handleUpsert = React.useCallback((upsertedTask: Task) => {
    modal.close();

    onSuccess?.(upsertedTask);
  }, [modal, onSuccess]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  if (isUpdate && !canUpdate) {
    return null;
  }

  return (
    <>
      {children
        ? children({ upsert: modal.open, label: operationLabel })
        : (
          <Action
            aria-label={`${operationLabel} attività`}
            title={`${operationLabel} attività`}
            label={operationLabel}
            size="S"
            onClick={() => modal.open()}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`${operationLabel} attività${task?.reference ? ` ${referenceTypeLabels[task.reference.type].toLowerCase()}` : ''}`}
          aria-label={`Modale per ${isUpdate ? 'modificare' : 'aggiungere'} l'attività`}
          footer={(
            <Action
              label={operationLabel}
              color="primary"
              emphasis="high"
              aria-label={'Pulsante per salvare l\'attività'}
              onClick={submitForm}
            />
          )}
        >
          <Stack gap={300}>
            {task?.reference?.type === ReferenceType.PROPERTY && (
              <ViewPropertyPreview propertyId={parseInt(task.reference.id, 10)} />
            )}
            <UpsertTask
              task={task}
              formRef={formRef}
              onSuccess={handleUpsert}
            />
          </Stack>
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertTaskAction;
